import React, { useCallback, useRef, useState } from 'react';
import { StyleSheet, Text, View, Platform, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';

import { isPhone, controlAPICall, isWeb, formatDateToLocalDateTime } from '@helpers';
import { getImageSize, computeTimeIndicators } from './helpers';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { scale } from '@components/frame';

import Image from '@v2/context/Image/context';
import TouchableElement from '@components/common/touchableElement';
import BatchNumberWithStatus from '@v2/context/ProductionBatch/tile/components/BatchNumberWithStatus';
import ActualTotalWithProgressIndicator from '@v2/context/ProductionBatch/tile/components/ActualTotalWithProgressIndicator';
import { connect } from 'react-redux';
import { productionBatchStatistics } from '@reducers/statistics';
import { useFocusEffect } from '@react-navigation/native';
import { setBatchSquareSize } from '@reducers/batch';
import Svg from '@styles/icons/svg';
import { SmallLoading } from '@components/loading';
import { DOCUMENT_ENTITY_TYPE, IMAGE_HANDLING_CONTEXT } from '@components/imageHandling/common';
import { LevelSubScreen } from '@v2/pages/common/enums';
import { TEST_IDS, TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import StatusBadge from '@components/common/StatusBadge';

const Item = ({
  isEmpty,
  itemSize = TILE_CONFIG.BASE_WIDTH,
  item,
  C,
  isSmallMode,
  isLineMode,
  generateImageURL,
  open,
  isSelected,
  selectedItem,
  isWithERP,
  offset,
  productionBatchStatistics,
  firstVisibleIndex,
  lastVisibleIndex,
  index,
  batchList,
  setBatchSquareSize,
  noSearchScreen,
  attachMode,
}) => {
  const style = {
    opacity: isEmpty ? 0 : 1,
    marginBottom: scale(isSmallMode ? TILE_CONFIG.MARGIN_BOTTOM_SMALL : TILE_CONFIG.MARGIN_BOTTOM_NORMAL),
  };

  const abortControllerBatchStatistic = useRef();
  const [batchStatistics, setBatchStatistics] = useState(null);

  useFocusEffect(
    useCallback(() => {
      if (isLineMode || isSmallMode) return;
      let timeoutId;

      const fetchBatchStatistics = async () => {
        const d = {
          productionBatchId: item.productionBatchId,
        };
        const result = await controlAPICall(productionBatchStatistics, d, abortControllerBatchStatistic);
        setBatchStatistics(result?.statistics);
      };

      const wasLoaded = batchList.find((b) => b.productionBatchId === batchStatistics?.productionBatchId);

      if (index >= firstVisibleIndex && index <= lastVisibleIndex && !wasLoaded) {
        timeoutId = setTimeout(() => {
          fetchBatchStatistics();
        }, 500);
      }

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, [firstVisibleIndex, lastVisibleIndex, index, isLineMode, isSmallMode])
  );

  if (itemSize) {
    style.width = itemSize;
  }
  const imgSize = getImageSize(isLineMode, itemSize, scale);

  const imageData = {
    image: {
      documentId: item.imageDocumentId,
      imageHash: item.imageHash,
      imageData: {
        isCropped: item.imageData?.isCropped,
        extensionDerivative: item.imageData?.extensionDerivative,
        pageNumber: item.imageData?.pageNumber,
        rotationAngle: item.imageData?.rotationAngle || 0,
        rotationAngleCrop: item.imageData?.rotationAngle || 0,
      },
      width: imgSize.width,
      height: imgSize.height,
      contextId: item.productionBatchId,
      contextCode: IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.Batch],
      borderRadius: TILE_CONFIG.BORDER_RADIUS,
    },
    onPress: () => open(item, LevelSubScreen.DOCUMENTS),
    showImageControl: true,
    attachMode,
  };

  if (isLineMode) {
    if (isEmpty) return null;
    return (
      <TouchableElement testID={TEST_IDS.searchScreen.productionBatches.lineTile} onPress={() => open(item)}>
        {({ pressHoverColor, boxStyle }) => (
          <View
            style={[
              boxStyle,
              {
                width: itemSize,
                alignItems: 'center',
                marginBottom: scale(18),
                flexDirection: 'row',
                backgroundColor: C(pressHoverColor('Background/Light/System')),
                padding: scale(isPhone || !noSearchScreen ? 0 : 8),
              },
            ]}
          >
            <View testID={TEST_IDS.searchScreen.productionBatches.lineImage} style={{ marginRight: scale(10), maxWidth: '100%' }}>
              <Image {...imageData} />
            </View>

            <View style={{ flex: 1, flexDirection: 'row', overflow: 'hidden' }}>
              <Text style={[font['Button 1'], { color: C('Label/Light/Primary'), flexShrink: 0 }]}>{item.batchNumber}</Text>
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={[
                  font['Subhead 1'],
                  {
                    color: C('Label/Light/Tertiary'),
                    flexShrink: 1,
                    overflow: 'hidden',
                  },
                ]}
              >
                {' '}
                – {item.ncFileName}
              </Text>
            </View>
          </View>
        )}
      </TouchableElement>
    );
  }

  const statistics = {
    actualTotalTimeTotalInSeconds: batchStatistics?.actualTotalTimeTotalInSeconds,
    scheduledTotalTimeTotalInSeconds: batchStatistics?.scheduledTotalTimeTotalInSeconds,
  };

  const {
    computedIdealTimePercentage,
    computedPaidTimePercentage,
    shouldHideIdeal,
    shouldHidePaid,
    shouldDisplayMinusForIdeal,
    shouldDisplayMinusForPaid,
  } = computeTimeIndicators({ item, batchStatistics, isWithERP });

  return isEmpty ? (
    <View style={[styles.itemComponent, style]} />
  ) : (
    <View
      onLayout={(event) => {
        const { height, width } = event.nativeEvent.layout;
        setBatchSquareSize({ height, width });
      }}
      style={[
        styles.itemComponent,
        style,
        // isSelected && { borderWidth: scale(1), borderColor: C('System/Light/Blue'), borderRadius: scale(TILE_CONFIG.BORDER_RADIUS) },
      ]}
    >
      <View
        testID={TEST_IDS.searchScreen.productionBatches.squareTileImage}
        style={[imgSize, { backgroundColor: C('Background/Light/Tile No Picture'), borderRadius: scale(TILE_CONFIG.BORDER_RADIUS) }]}
      >
        <Image {...imageData} />
      </View>
      {!isSmallMode && (
        <TouchableOpacity testID={TEST_IDS.searchScreen.productionBatches.squareTileTile} activeOpacity={1} onPress={() => open(item)}>
          <View style={{ paddingHorizontal: scale(9), paddingTop: scale(5), gap: scale(7) }}>
            <View style={styles.itemLineContainer}>
              <BatchNumberWithStatus {...{ batch: item, C, hovered: false, selectedItem, isWithERP }} />
              <StatusBadge C={C} isClosed={item?.isClosed} isInProgress={item?.isInProgress} />
            </View>
            <View style={[styles.itemLineContainer, { minHeight: scale(23) }]}>
              <View style={{ paddingTop: scale(2) }}>
                <ActualTotalWithProgressIndicator {...{ batch: item, C, offset, statistics, fontForDigit: 'Time 2', fontForSecondDigit: 'Time 4' }} />
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'baseline',
                  height: scale(20),
                  transform: [{ translateY: scale(-2) }],
                }}
              >
                <Text
                  style={[
                    font['Time 2'],
                    { color: C('Label/Light/Primary') },
                    { ...(!isWeb ? { paddingVertical: scale(2), transform: [{ translateY: scale(-2) }] } : {}) },
                  ]}
                >
                  {item?.quantity}
                </Text>
                <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}> {i18n.t('Pcs')}</Text>
              </View>
            </View>
            <View style={[styles.itemLineContainer, { minHeight: scale(23) }]}>
              <View style={[styles.basicContainer, { gap: scale(11.5) }]}>
                {!shouldHideIdeal && (
                  <View style={[styles.basicContainer, { gap: scale(2.5) }]}>
                    <Svg icon="runningBunny" width={scale(16)} height={scale(20)} color={C('Label/Light/Tertiary')} transform="translate(0, 1)" />
                    {!computedIdealTimePercentage && <SmallLoading />}
                    {computedIdealTimePercentage && (
                      <>
                        <Svg
                          {...{
                            icon: 'middleArrow',
                            width: scale(10),
                            height: scale(22),
                            color: C('Label/Light/Tertiary'),
                            ...(shouldDisplayMinusForIdeal ? { transform: 'rotate(180) translate(0, -1)' } : {}),
                          }}
                        />
                        <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{computedIdealTimePercentage}</Text>
                      </>
                    )}
                  </View>
                )}
                {!shouldHidePaid && (
                  <View style={[styles.basicContainer, { gap: scale(2.5) }]}>
                    <Svg icon="euro" width={scale(11)} height={scale(20)} color={C('Status/Light/Paid')} transform="translate(0, 0.5)" />
                    {!computedPaidTimePercentage && <SmallLoading />}
                    {computedPaidTimePercentage && (
                      <>
                        <Svg
                          {...{
                            icon: 'middleArrow',
                            width: scale(10),
                            height: scale(22),
                            color: C('Label/Light/Tertiary'),
                            ...(shouldDisplayMinusForPaid ? { transform: 'rotate(180) translate(0, -1)' } : {}),
                          }}
                        />
                        <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{computedPaidTimePercentage}</Text>
                      </>
                    )}
                  </View>
                )}
              </View>
              <View style={[styles.basicContainer, { gap: scale(5) }]}>
                <Svg icon="runningMan" width={scale(10)} height={scale(13)} color={C('Label/Light/Tertiary')} transform="translate(0, 1)" />
                <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>
                  {formatDateToLocalDateTime(item?.startedOn, false, false)}
                </Text>
              </View>
            </View>
            <View style={styles.itemLineContainer}>
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={[font['Subhead 1'], { color: C('Label/Light/Tertiary'), flex: 1, marginRight: scale(8) }]}
              >
                {item.machineShortName}
              </Text>
              <View style={[styles.basicContainer, { gap: scale(5), flexShrink: 0 }]}>
                <Svg icon="finishFlag" width={scale(14)} height={scale(18)} color={C('Label/Light/Tertiary')} transform="translate(0, 1)" />
                <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>
                  {formatDateToLocalDateTime(item?.endedOn, false, false)}
                </Text>
              </View>
            </View>
          </View>
        </TouchableOpacity>
      )}
    </View>
  );
};

export default connect(
  ({
    machines: { selectedItem },
    membership: { isWithERP },
    batch: { batchList },
    time: {
      server: { offset },
    },
  }) => ({ selectedItem, isWithERP, offset, batchList }),
  { productionBatchStatistics, setBatchSquareSize }
)(ColorWrapper(Item));

const styles = StyleSheet.create({
  basicContainer: { flexDirection: 'row', alignItems: 'center' },
  itemComponent: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemLineContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});
